import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  ShieldCheck,
  Globe2,
  Sparkles,
  Copy,
  Smartphone,
} from "lucide-react";
import { PAIRS, LANG_META, PHRASE_LIBRARY, LANGUAGE_INFO } from "@/lib/languages";
import { useSeo } from "@/lib/useSeo";
import Translator from "@/components/site/Translator";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const HERO_BG_STYLE = {
  backgroundImage: `radial-gradient(circle at 20% 50%, hsl(222 100% 50% / 0.04) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, hsl(222 80% 60% / 0.03) 0%, transparent 40%),
    radial-gradient(circle at 60% 80%, hsl(222 60% 70% / 0.03) 0%, transparent 45%)`,
};

const homeFaqs = [
  {
    q: "Which Indian languages does Tiny Stem support?",
    a: "We currently support translations between English and 6 Indian languages: Hindi, Tamil, Marathi, Malayalam, Telugu and Gujarati. Both directions (English → Indian language and Indian language → English for Hindi) are available on dedicated pages.",
  },
  {
    q: "Is the translator really free? Do I need to sign up?",
    a: "Yes, Tiny Stem is 100% free with no signup required. No credit card, no email — just type your text and click Translate. There are no daily quotas for everyday personal use.",
  },
  {
    q: "How accurate are the translations?",
    a: "We use a state-of-the-art AI model tuned for Indian languages. For everyday sentences, chats, emails and blog content, expect near-native accuracy. For legal, medical or financial documents we recommend a human review as a final step.",
  },
  {
    q: "Do you store or read the text I translate?",
    a: "No. We do not store, log or use your translations for training. Your text is sent to the AI provider only for the purpose of generating the translation and is discarded immediately after.",
  },
  {
    q: "Can I copy, paste or download the translation?",
    a: "Yes. Every input and output box has quick action buttons for Copy, Paste, Download (.txt) and Reset, so you can move text in and out of the translator effortlessly.",
  },
];

const homeKeyFeatures = [
  {
    icon: Zap,
    title: "Instant translation",
    body: "Natural-sounding results in under 2 seconds. Works for one-liners and 5,000-character essays alike.",
  },
  {
    icon: Sparkles,
    title: "Context-aware AI",
    body: "Preserves tone, honorifics and idioms across 7 Indian languages — far beyond word-for-word tools.",
  },
  {
    icon: ShieldCheck,
    title: "Free & private",
    body: "No signup, no watermark and nothing you type is stored, logged or used for training.",
  },
  {
    icon: Copy,
    title: "Copy, paste, download",
    body: "Every input and output box has one-click Copy, Paste, Download and Reset actions.",
  },
  {
    icon: Globe2,
    title: "7 languages ready",
    body: "Hindi, Tamil, Marathi, Malayalam, Telugu, Gujarati and English — each with a dedicated SEO page.",
  },
  {
    icon: Smartphone,
    title: "Mobile-friendly",
    body: "Responsive across phones, tablets and desktops with no app install required.",
  },
];

export default function Home() {
  useSeo({
    title:
      "Free Online Translator | Hindi, Tamil, Marathi, Telugu, Malayalam & Gujarati",
    description:
      "Free AI-powered translator for 7 languages. Translate Hindi to English, English to Hindi, Tamil, Marathi, Telugu, Malayalam and Gujarati instantly.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          name: "tinystem.com",
          url: typeof window !== "undefined" ? window.location.origin : "",
        },
        {
          "@type": "FAQPage",
          mainEntity: homeFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 pointer-events-none"
          style={HERO_BG_STYLE}
          aria-hidden
        />
        <div className="container-x relative pt-16 lg:pt-24 pb-10">
          <p
            data-testid="hero-overline"
            className="overline mb-6"
          >
            Tiny Stem · AI Translator
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
            Translate <span className="text-primary">every</span> Indian language.
            <br /> Free, fast, human-grade.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Instantly translate between Hindi, Tamil, Marathi, Telugu, Malayalam,
            Gujarati and English. Built for students, creators and businesses across India.
          </p>

          <div className="mt-10 max-w-5xl">
            <Translator defaultSource="english" defaultTarget="hindi" />
          </div>
        </div>
      </section>

      {/* LANGUAGE PAIRS GRID */}
      <section className="container-x section-y">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="overline">All translations</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              Pick a language pair
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">
            Each language pair has a dedicated page with common phrases, examples and FAQs — all optimised for speed & accuracy.
          </p>
        </div>

        <div
          data-testid="language-pairs-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-border border border-border rounded-xl overflow-hidden"
        >
          {PAIRS.map((p) => {
            const src = LANG_META[p.source];
            const tgt = LANG_META[p.target];
            return (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`pair-card-${p.slug}`}
                className="group bg-white p-8 hover:bg-secondary transition-colors flex flex-col justify-between min-h-[200px]"
              >
                <div>
                  <div className="flex items-baseline gap-3 font-display font-black text-3xl tracking-tighter">
                    <span>{src.native}</span>
                    <ArrowRight
                      className="h-5 w-5 text-primary translate-y-[-2px] group-hover:translate-x-1 transition-transform"
                      strokeWidth={2}
                    />
                    <span>{tgt.native}</span>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">
                    {p.keyword}
                  </p>
                </div>
                <span className="mt-6 text-sm font-semibold text-primary inline-flex items-center gap-1">
                  Translate now
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 1 · COMMON PHRASES */}
      <section
        id="common-phrases"
        data-testid="section-common-phrases"
        className="container-x section-y"
      >
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline">Common phrases</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              Everyday English translated for you
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">
            Handy translations of the most common English phrases across all 10
            supported languages — Indian and international. Tap a tab to switch.
          </p>
        </div>

        <Tabs defaultValue="hindi" className="w-full" data-testid="common-phrases-tabs">
          <TabsList className="flex flex-wrap h-auto bg-secondary p-1 rounded-lg">
            {["hindi","tamil","marathi","malayalam","telugu","gujarati","spanish","french","mandarin","german","mizo"].map((k) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`phrases-tab-${k}`}
                className="text-sm capitalize"
              >
                {LANG_META[k].name}
              </TabsTrigger>
            ))}
          </TabsList>
          {["hindi","tamil","marathi","malayalam","telugu","gujarati","spanish","french","mandarin","german","mizo"].map((k) => (
            <TabsContent key={k} value={k} className="mt-6">
              <div className="bg-white border border-border rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/2">English</TableHead>
                      <TableHead className="w-1/2">{LANG_META[k].name} ({LANG_META[k].native})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {PHRASE_LIBRARY.english.map((phrase, i) => (
                      <TableRow key={i}>
                        <TableCell className="align-top py-4">{phrase}</TableCell>
                        <TableCell className="align-top py-4 font-mono text-[15px]">
                          {PHRASE_LIBRARY[k][i]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* 2 · KEY FEATURES */}
      <section
        id="key-features"
        data-testid="section-key-features"
        className="bg-secondary border-y border-border"
      >
        <div className="container-x section-y">
          <p className="overline">Key features</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
            Everything you need in a translator, done well.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {homeKeyFeatures.map((f) => (
              <div
                key={f.title}
                className="bg-white p-8 h-full flex flex-col"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <f.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-xl font-semibold tracking-tight">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · ABOUT OUR LANGUAGES */}
      <section
        id="about-languages"
        data-testid="section-about-languages"
        className="container-x section-y"
      >
        <p className="overline">About our languages</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
          A short guide to the languages we translate
        </h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Each language carries its own history, script and cultural rhythm. Pick one
          below to learn a little about it before you translate.
        </p>

        <Tabs defaultValue="hindi" className="mt-10 w-full" data-testid="about-lang-tabs">
          <TabsList className="flex flex-wrap h-auto bg-secondary p-1 rounded-lg">
            {["hindi","tamil","marathi","malayalam","telugu","gujarati","spanish","french","mandarin","german","mizo"].map((k) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`about-tab-${k}`}
                className="text-sm capitalize"
              >
                {LANG_META[k].name}
              </TabsTrigger>
            ))}
          </TabsList>
          {["hindi","tamil","marathi","malayalam","telugu","gujarati","spanish","french","mandarin","german","mizo"].map((k) => {
            const info = LANGUAGE_INFO[k];
            return (
              <TabsContent key={k} value={k} className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
                  <div className="lg:col-span-2 space-y-5 text-muted-foreground leading-relaxed">
                    <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground">
                      {LANG_META[k].name} ({LANG_META[k].native})
                    </h3>
                    {info.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  <aside>
                    <div className="border border-border bg-white p-6 rounded-xl">
                      <dl className="space-y-3 text-sm">
                        <div className="flex flex-col gap-1">
                          <dt className="text-muted-foreground text-xs uppercase tracking-wider">Script</dt>
                          <dd className="font-semibold">{LANG_META[k].script}</dd>
                        </div>
                        <div className="flex flex-col gap-1 pt-3 border-t border-border">
                          <dt className="text-muted-foreground text-xs uppercase tracking-wider">Family</dt>
                          <dd className="font-semibold">{info.family}</dd>
                        </div>
                        <div className="flex flex-col gap-1 pt-3 border-t border-border">
                          <dt className="text-muted-foreground text-xs uppercase tracking-wider">Speakers</dt>
                          <dd className="font-semibold">{info.speakers}</dd>
                        </div>
                        <div className="flex flex-col gap-1 pt-3 border-t border-border">
                          <dt className="text-muted-foreground text-xs uppercase tracking-wider">Region</dt>
                          <dd className="font-semibold">{info.region}</dd>
                        </div>
                      </dl>
                    </div>
                  </aside>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </section>

      {/* 4 · FAQ */}
      <section
        id="faq"
        data-testid="section-faq"
        className="bg-secondary border-y border-border"
      >
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            <div className="lg:col-span-1">
              <p className="overline">FAQ</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                Frequently asked questions
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Everything you need to know about Tiny Stem. Can't find your
                answer?{" "}
                <Link
                  to="/contact"
                  className="text-primary font-medium link-underline"
                  data-testid="faq-contact-link"
                >
                  Get in touch.
                </Link>
              </p>
            </div>
            <div className="lg:col-span-2">
              <Accordion type="single" collapsible data-testid="home-faq-accordion">
                {homeFaqs.map((f, i) => (
                  <AccordionItem key={i} value={`home-faq-${i}`}>
                    <AccordionTrigger className="text-left font-semibold text-base">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="container-x py-24 lg:py-28 flex flex-col items-start gap-6">
          <p className="overline text-zinc-400">Ready?</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter max-w-3xl">
            Break language barriers in one click.
          </h2>
          <p className="text-zinc-300 max-w-xl">
            Explore all 7 dedicated translation pages — each hand-crafted for the
            language pair you need.
          </p>
          <div className="flex flex-wrap gap-3 mt-4" data-testid="cta-pair-chips">
            {PAIRS.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="text-sm font-medium px-4 py-2 border border-zinc-700 rounded-full hover:bg-white hover:text-zinc-950 transition-colors"
              >
                {p.keyword}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
